"use client";

import React from "react";
import { Button, buttonVariants } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { CheckCircle2, Mail, MessageSquare, Send } from "lucide-react";

import Link from "next/link";
import { z } from "zod/v3";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { type Session } from "next-auth";
import { PublicPageHeader } from "~/components/public-page-header";
import { api } from "~/trpc/react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";

const contactSchema = z.object({
  name: z.string().min(1, "Unesi ime i prezime"),
  email: z.string().email("Unesi ispravnu email adresu"),
  subject: z.string().min(1, "Izaberi predmet"),
  message: z.string().min(1, "Napiši poruku"),
});

const subjects = [
  "Osnove i poverenje",
  "Izbor terapeuta i prva sesija",
  "Zakazivanje i sesije",
  "Cene i plaćanje",
  "Privatnost i nalog",
  "AI alati",
  "Drugo",
];

export default function ContactSupportPage(props: { session: Session | null }) {
  const form = useForm<z.infer<typeof contactSchema>>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: props.session?.user?.name ?? "",
      email: props.session?.user?.email ?? "",
      subject: "",
      message: "",
    },
  });

  const contactSupport = api.marketing.contactSupport.useMutation();

  function onSubmit(values: z.infer<typeof contactSchema>) {
    contactSupport.mutate(values);
  }

  const sentEmail = form.getValues("email");

  return (
    <div className="min-h-screen bg-background">
      <PublicPageHeader
        breadcrumbItems={[
          { label: "Početna", href: "/" },
          { label: "Podrška", href: "/podrska" },
          { label: "Kontakt" },
        ]}
        density="compact"
        title="Piši podršci"
        subtitle="Piši ovde kad ti AI asistent i podrška nisu rešili pitanje. Odgovaramo u roku od 24h."
      />

      <div className="max-w-8xl container mx-auto px-4 py-8 md:px-8 md:py-12">
        <div className="mb-8 rounded-lg border border-brand/20 bg-brand/5 p-4 md:p-6">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <MessageSquare className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
              <div>
                <p className="font-medium text-foreground">
                  Treba ti brz odgovor? Postavi pitanje asistentu prvo
                </p>
                <p className="text-sm text-muted-foreground">
                  Pitanja o cenama, zakazivanju, terapiji ili platformi rešava
                  odmah. Ako treba, uključuje tim podrške.
                </p>
              </div>
            </div>
            <Link
              href="/asistent"
              className={buttonVariants({
                variant: "cta",
                size: "sm",
                className: "shrink-0",
              })}
            >
              Postavi pitanje
            </Link>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <Card>
            <CardContent className="p-6">
              {contactSupport.isSuccess ? (
                <div className="flex flex-col items-start gap-3 py-6">
                  <CheckCircle2 className="h-8 w-8 text-brand" />
                  <h2 className="text-xl font-semibold">Poruka je poslata</h2>
                  <p className="text-muted-foreground">
                    Tvoja poruka na temu{" "}
                    <span className="font-medium text-foreground">
                      {form.getValues("subject")}
                    </span>{" "}
                    je stigla do nas. Odgovaramo ti na{" "}
                    <span className="font-medium text-foreground">
                      {sentEmail}
                    </span>{" "}
                    u roku od 24h.
                  </p>
                </div>
              ) : (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Ime i prezime</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Unesi ime i prezime"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email adresa</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Unesi email adresu"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Predmet</FormLabel>
                          <FormControl>
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                            >
                              <SelectTrigger id="subject">
                                <SelectValue placeholder="Izaberi predmet" />
                              </SelectTrigger>
                              <SelectContent>
                                {subjects.map((subject) => (
                                  <SelectItem key={subject} value={subject}>
                                    {subject}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Poruka</FormLabel>
                          <FormControl>
                            <Textarea
                              id="message"
                              placeholder="Detaljno opiši svoje pitanje"
                              {...field}
                              rows={6}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {contactSupport.isError ? (
                      <p className="text-sm text-destructive">
                        Slanje poruke nije potvrđeno. Piši nam direktno na{" "}
                        <span className="font-medium">
                          podrska@resimoprobleme.com
                        </span>
                        .
                      </p>
                    ) : null}
                    <Button
                      type="submit"
                      variant="cta"
                      className="w-full sm:w-auto"
                      disabled={contactSupport.isPending}
                      isLoading={contactSupport.isPending}
                      icon={Send}
                    >
                      Pošalji poruku
                    </Button>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>

          <aside className="space-y-5">
            <div className="rounded-lg border p-4">
              <p className="text-sm font-semibold">Šta se dešava posle</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Poruku čita tim za podršku, ne bot. Odgovor ide na email koji
                ostaviš u formularu.
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm font-semibold">
                Preferiraš direktan email?
              </p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Piši nam direktno, bez formulara.
              </p>
              <Link
                href="mailto:podrska@resimoprobleme.com"
                className={buttonVariants({
                  variant: "outline",
                  size: "sm",
                  className: "mt-3 gap-2",
                })}
              >
                <Mail className="h-4 w-4" />
                podrska@resimoprobleme.com
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
